import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Plus, ClipboardText, Warning, CaretLeft, ListChecks, Tray, Barcode, ChartBar,
} from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { useAuth, canRequestParts } from '@/auth/AuthContext';
import { shizaiApi } from './shizaiApi';
import PicRequests from './PicRequests';
import PicReceiving from './PicReceiving';
import ProcurementReport from './ProcurementReport';
import { NewRequest } from './partsRequest/NewRequest';
import { MyRequests } from './partsRequest/MyRequests';
import { EditRequestDialog } from './partsRequest/EditRequestDialog';

export { EditRequestDialog };

/**
 * 部品受注入力 — Milestone D
 * Standalone page for regular employees to submit parts-purchase requests.
 * Shopping-list style form (multi-line) + a "my requests" tracking tab.
 * The buyer sees these inside /shizai/requests as-normal.
 * iter_178 · refactored: form/lines/list/edit components live in ./partsRequest/.
 */
export default function PartsRequest() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const deepLinkReq = searchParams.get('req') || '';
  const [tab, setTab] = useState(() => (deepLinkReq ? 'pic' : (searchParams.get('tab') === 'receive' ? 'receive' : 'new')));
  const [categories, setCategories] = useState([]);
  const [isPic, setIsPic] = useState(false);
  const [editReq, setEditReq] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    shizaiApi.orderCategories()
      .then((cats) => setCategories(cats.filter((c) => c.active !== false)))
      .catch((err) => console.error('[parts-request] orderCategories failed', err));
    shizaiApi.picAccess()
      .then((a) => setIsPic(!!a?.is_pic))
      .catch(() => setIsPic(false));
  }, []);

  const canReq = canRequestParts(user);
  useEffect(() => {
    if (!canReq && isPic) setTab('pic');
  }, [canReq, isPic]);

  if (!canReq && !isPic) {
    return (
      <div className="min-h-screen bg-zinc-50 flex items-center justify-center px-6">
        <div className="bg-white rounded-lg shadow-sm p-8 max-w-md text-center border border-zinc-200" data-testid="pr-no-access">
          <Warning size={48} weight="duotone" className="text-rose-500 mx-auto mb-3" />
          <h2 className="text-lg font-bold text-zinc-900 mb-1">アクセス権限がありません</h2>
          <p className="text-sm text-zinc-500 mb-4">部品受注入力を使用するには、管理者に「部品受注入力」の権限を有効にしてもらってください。</p>
          <Button variant="outline" onClick={() => navigate('/')}>
            <CaretLeft size={16} className="mr-1.5" /> 戻る
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="relative" data-testid="parts-request-page">
      {/* Decorative blobs live in their own clipped layer so they can't cause
          page overflow AND can't clip content (e.g. the line-number badges). */}
      <div className="pointer-events-none absolute inset-0 overflow-hidden -z-10" aria-hidden="true">
        <div className="absolute -top-16 -right-16 w-72 h-72 bg-rose-200/30 rounded-full blur-3xl" />
        <div className="absolute -bottom-16 -left-16 w-72 h-72 bg-orange-200/30 rounded-full blur-3xl" />
      </div>

      <header className="mb-6">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-lg bg-teal-700 flex items-center justify-center shadow-md ring-1 ring-black/5">
            <ClipboardText size={26} weight="duotone" className="text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold tracking-tight text-zinc-900">部品受注入力</h1>
            <p className="text-sm text-zinc-500">必要な部品を資材担当へ依頼します。</p>
          </div>
        </div>
      </header>

      {/* Tabs — scrollable on narrow screens so labels never wrap mid-word */}
      <div className="flex max-w-full overflow-x-auto items-center gap-1 bg-white border border-zinc-200 rounded-full p-1 shadow-sm mb-6">
        {canReq && (
        <button
          onClick={() => setTab('new')}
          data-testid="pr-tab-new"
          className={`px-4 py-2 rounded-full text-sm font-medium transition-colors flex items-center gap-1.5 whitespace-nowrap shrink-0 ${tab === 'new' ? 'bg-zinc-900 text-white shadow' : 'text-zinc-600 hover:text-zinc-900'}`}
        >
          <Plus size={14} weight="bold" /> 新規依頼
        </button>
        )}
        {canReq && (
        <button
          onClick={() => setTab('mine')}
          data-testid="pr-tab-mine"
          className={`px-4 py-2 rounded-full text-sm font-medium transition-colors flex items-center gap-1.5 whitespace-nowrap shrink-0 ${tab === 'mine' ? 'bg-zinc-900 text-white shadow' : 'text-zinc-600 hover:text-zinc-900'}`}
        >
          <ListChecks size={14} weight="bold" /> マイリクエスト
        </button>
        )}
        {isPic && (
          <button
            onClick={() => setTab('pic')}
            data-testid="pr-tab-pic"
            className={`px-4 py-2 rounded-full text-sm font-medium transition-colors flex items-center gap-1.5 whitespace-nowrap shrink-0 ${tab === 'pic' ? 'bg-zinc-900 text-white shadow' : 'text-zinc-600 hover:text-zinc-900'}`}
          >
            <Tray size={14} weight="bold" /> 受信依頼
          </button>
        )}
        {isPic && (
          <button
            onClick={() => setTab('receive')}
            data-testid="pr-tab-receive"
            className={`px-4 py-2 rounded-full text-sm font-medium transition-colors flex items-center gap-1.5 whitespace-nowrap shrink-0 ${tab === 'receive' ? 'bg-zinc-900 text-white shadow' : 'text-zinc-600 hover:text-zinc-900'}`}
          >
            <Barcode size={14} weight="bold" /> 入荷スキャン
          </button>
        )}
        {isPic && (
          <button
            onClick={() => setTab('report')}
            data-testid="pr-tab-report"
            className={`px-4 py-2 rounded-full text-sm font-medium transition-colors flex items-center gap-1.5 whitespace-nowrap shrink-0 ${tab === 'report' ? 'bg-zinc-900 text-white shadow' : 'text-zinc-600 hover:text-zinc-900'}`}
          >
            <ChartBar size={14} weight="bold" /> レポート
          </button>
        )}
      </div>

      {tab === 'new' && canReq && <NewRequest categories={categories} onSubmitted={() => setTab('mine')} />}
      {tab === 'mine' && canReq && <MyRequests key={`mine-${reloadKey}`} categories={categories} onEdit={setEditReq} />}
      {tab === 'pic' && isPic && <PicRequests key={`pic-${reloadKey}`} categories={categories} onEdit={setEditReq} initialQuery={deepLinkReq} />}
      {tab === 'receive' && isPic && <PicReceiving />}
      {tab === 'report' && isPic && <ProcurementReport />}

      {editReq && (
        <EditRequestDialog
          req={editReq}
          categories={categories}
          onClose={() => setEditReq(null)}
          onSaved={() => { setEditReq(null); setReloadKey((k) => k + 1); }}
        />
      )}
    </div>
  );
}
